import ConversationPage from '@components/chatbox/ConversationPage';
import CloseIcon from '@mui/icons-material/Close';
import MessageIcon from '@mui/icons-material/Message';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { getDateFormat, getTimeFormat } from '@utils/dateHelper';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CancelReservationDialog from './CancelReservationDialog';
import NotifyRearrangeDialog from './NotifyRearrangeDialog';
import { notifyRearrange } from '@services/reservationService';
import { SelectChangeEvent } from '@mui/material/Select';

interface ReservationUser {
  _id: string;
  email: string;
  displayName: string;
  phone: string;
}

interface ReservationPlan {
  _id: string;
  name: string;
  zh_name: string;
}

interface ReservationCoupon {
  _id: string;
}

interface ReservationStaff {
  _id: string;
}

interface Reservation {
  _id: string;
  staffId: ReservationStaff;
  customerName: string;
  customerPhone?: string;
  start: string;
  end: string;
  userId?: ReservationUser;
  planId: ReservationPlan;
  status: string;
  createdAt: string;
  remark?: string;
  createdType: string;
  paymentType?: string;
  couponId?: ReservationCoupon;
  originalAmount?: number;
  discountedAmount?: number;
  deductCredit?: number;
}

interface Plan {
  _id: string;
  name: string;
  price?: number;
}

interface CancelData {
  cancelledReason: string;
  cancelledRemark: string;
}

interface ReservationFormData {
  _id: string;
  planId: string;
  customerName: string;
  customerPhone: string;
  date: string;
  timeSlots: string;
  remark: string;
  status: string;
  createdType: string;
  paymentType: string;
  couponId: string;
  originalAmount: string | number;
  discountedAmount: string | number;
  deductCredit: string;
}

export interface ReservationModalProps {
  reservation: Reservation;
  onClose: () => void;
  onRearrange: (reservation: Reservation) => void;
  onSubmit: (data: ReservationFormData) => void | Promise<void>;
  onApprove: (reservation: Reservation) => void;
  onDelete: (reservation: Reservation) => void;
  onCancel: (data: CancelData) => void;
  plans: Plan[];
}

const ReservationModal = ({ reservation, onClose, onRearrange, onSubmit, onApprove, onDelete, onCancel, plans }: ReservationModalProps) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);

  const handleChatClick = () => {
    setShowNotifyDialog(true);
  };

  const handleCloseChatDialog = () => {
    setShowChatDialog(false);
  };

  const handleCloseNotifyDialog = () => {
    setShowNotifyDialog(false);
  };

  const handleConfirmNotify = async () => {
    setIsNotifying(true);
    try {
      await notifyRearrange(reservation._id);
      setShowNotifyDialog(false);
      setShowChatDialog(true);
    } catch (error) {
      console.error('Error notifying for rearrangement:', error);
      // TODO: show error to user
    } finally {
      setIsNotifying(false);
    }
  };

  const handleCancelConfirm = (cancelData: CancelData) => {
    onCancel(cancelData);
    onClose();
  };

  const methods = useForm<ReservationFormData>({
    defaultValues: {
      _id: reservation._id || '',
      planId: reservation.planId?._id || '',
      customerName: reservation.customerName || '',
      customerPhone: reservation.customerPhone || reservation.userId?.phone || '',
      date: getDateFormat(new Date(reservation.start)) || '',
      timeSlots: reservation.start && reservation.end ?
        `${getTimeFormat(reservation.start)} - ${getTimeFormat(reservation.end)}` : '',
      remark: reservation.remark || '',
      status: reservation.status || '',
      createdType: reservation.createdType || '',
      paymentType: reservation.paymentType || '',
      couponId: reservation.couponId?._id || '',
      originalAmount: reservation.originalAmount ? `${reservation.originalAmount}` : '',
      discountedAmount: reservation.discountedAmount ? `${reservation.discountedAmount}` : '',
      deductCredit: reservation.deductCredit ? `${reservation.deductCredit}` : '',
    }
  });

  const selectedPlanId = methods.watch('planId');

  useEffect(() => {
    if (selectedPlanId && isEditMode) {
      const selectedPlan = plans.find(plan => plan._id === selectedPlanId);
      const currentOriginalAmount = methods.getValues('originalAmount');
      const currentDiscountedAmount = methods.getValues('discountedAmount');

      if (selectedPlan?.price) {
        if (!currentOriginalAmount) {
          methods.setValue('originalAmount', `${selectedPlan.price}`);
        }
        if (!currentDiscountedAmount) {
          methods.setValue('discountedAmount', `${selectedPlan.price}`);
        }
      }
    }
  }, [selectedPlanId, plans, isEditMode, methods]);

  const onSave = async (data: ReservationFormData) => {
    try {
      setIsSubmitting(true);
      const formattedData = {
        ...data,
        originalAmount: data.originalAmount ? Number(data.originalAmount) : data.originalAmount,
        discountedAmount: data.discountedAmount ? Number(data.discountedAmount) : data.discountedAmount,
      };
      await onSubmit(formattedData);
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving reservation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={ true } onClose={ onClose }>
        <DialogTitle>
          <Stack spacing={ 1 } direction="row" alignItems="center" sx={ { position: 'relative' } }>
            <Box>{ t('reservationsPage.editReservation') }</Box>
            {
              reservation.status === 'pending-for-approval' && (
                <Button
                  onClick={ (e) => {
                    e.preventDefault();
                    onApprove(reservation);
                    onClose();
                  } }
                  type="button"
                  variant="contained"
                  color="primary"
                >
                  { t('actions.approve') }
                </Button>
              )
            }
            <IconButton
              aria-label="close"
              onClick={ onClose }
              sx={ {
                position: 'absolute',
                right: -15,
                top: -5,
                color: (theme) => theme.palette.grey[500],
              } }
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={ methods.handleSubmit(onSave) } style={ { width: '100%' } }>
            <Controller
              name="customerName"
              control={ methods.control }
              rules={ { required: t('validation.customerNameRequired') } }
              render={ ({ field, fieldState: { error } }) => (
                <FormControl fullWidth margin="normal" error={ !!error }>
                  { isEditMode ? (
                    <>
                      <TextField
                        { ...field }
                        id="customerName"
                        label={ t('reservation.customerName') }
                        error={ !!error }
                      />
                      { error && <FormHelperText>{ error.message }</FormHelperText> }
                    </>
                  ) : (
                    <>
                      <InputLabel shrink id="customerName-label">{ t('reservation.customerName') }</InputLabel>
                      <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                        { field.value || t('common.na') }
                      </Typography>
                    </>
                  ) }
                </FormControl>
              ) }
            />

            <Controller
              name="customerPhone"
              control={ methods.control }
              rules={ {
                pattern: {
                  value: /^[0-9]{8}$/,
                  message: t('validation.phoneNumberFormat')
                }
              } }
              render={ ({ field, fieldState: { error } }) => (
                <FormControl fullWidth margin="normal" error={ !!error }>
                  { isEditMode ? (
                    <>
                      <TextField
                        { ...field }
                        id="customerPhone"
                        label={ t('reservation.phoneNumber') }
                        error={ !!error }
                      />
                      { error && <FormHelperText>{ error.message }</FormHelperText> }
                    </>
                  ) : (
                    <>
                      <InputLabel shrink id="customer-phone-label">{ t('reservation.phoneNumber') }</InputLabel>
                      <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                        { field.value || t('common.na') }
                      </Typography>
                    </>
                  ) }
                </FormControl>
              ) }
            />

            <Controller
              name="date"
              control={ methods.control }
              render={ ({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel shrink>{ t('reservation.date') }</InputLabel>
                  <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                    { field.value || t('common.na') }
                  </Typography>
                </FormControl>
              ) }
            />

            <Controller
              name="timeSlots"
              control={ methods.control }
              render={ ({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel shrink>{ t('reservation.timeSlots') }</InputLabel>
                  <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                    { field.value || t('common.na') }
                  </Typography>
                </FormControl>
              ) }
            />

            <Controller
              name="planId"
              control={ methods.control }
              rules={ { required: t('validation.planRequired') } }
              render={ ({ field, fieldState: { error } }) => (
                <FormControl fullWidth margin="normal" error={ !!error }>
                  <InputLabel shrink>{ t('reservation.plan') }</InputLabel>
                  { isEditMode ? (
                    <>
                      <Select
                        { ...field }
                        label={ t('reservation.plan') }
                        error={ !!error }
                        sx={ { mt: 1 } }
                        onChange={ (e: SelectChangeEvent) => {
                          field.onChange(e);
                          const selectedPlan = plans.find(plan => plan._id === e.target.value);
                          const currentOriginalAmount = methods.getValues('originalAmount');
                          const currentDiscountedAmount = methods.getValues('discountedAmount');

                          if (selectedPlan?.price) {
                            if (!currentOriginalAmount) {
                              methods.setValue('originalAmount', `${selectedPlan.price}`);
                            }
                            if (!currentDiscountedAmount) {
                              methods.setValue('discountedAmount', `${selectedPlan.price}`);
                            }
                          }
                        } }
                      >
                        { plans?.map((plan) => (
                          <MenuItem key={ plan._id } value={ plan._id }>
                            { plan.name }
                          </MenuItem>
                        )) }
                      </Select>
                      { error && <FormHelperText>{ error.message }</FormHelperText> }
                    </>
                  ) : (
                    <>
                      <InputLabel shrink id="plan-label">{ t('reservation.plan') }</InputLabel>
                      <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                        { reservation.planId?.name || t('common.na') }
                      </Typography>
                    </>
                  ) }
                </FormControl>
              ) }
            />

            { reservation.createdType === 'app-created' && (
              <Controller
                name="status"
                control={ methods.control }
                render={ ({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel shrink>{ t('reservation.status') }</InputLabel>
                    <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                      { field.value || t('common.na') }
                    </Typography>
                  </FormControl>
                ) }
              />
            ) }

            <Controller
              name="createdType"
              control={ methods.control }
              render={ ({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel shrink>{ t('reservation.createdType') }</InputLabel>
                  <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                    { field.value || t('common.na') }
                  </Typography>
                </FormControl>
              ) }
            />

            <Controller
              name="paymentType"
              control={ methods.control }
              render={ ({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel shrink>{ t('reservation.paymentType') }</InputLabel>
                  <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                    { field.value || t('common.na') }
                  </Typography>
                </FormControl>
              ) }
            />

            { reservation.couponId && (
              <Controller
                name="couponId"
                control={ methods.control }
                render={ ({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel shrink>{ t('reservation.couponId') }</InputLabel>
                    <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                      { field.value || t('common.na') }
                    </Typography>
                  </FormControl>
                ) }
              />
            ) }

            <Controller
              name="originalAmount"
              control={ methods.control }
              render={ ({ field, fieldState: { error } }) => (
                <FormControl fullWidth margin="normal" error={ !!error }>
                  { !(reservation.createdType === 'app-created') && isEditMode ? (
                    <>
                      <TextField
                        { ...field }
                        id="originalAmount"
                        label={ t('reservation.originalAmount') }
                        slotProps={ { inputLabel: { shrink: true } } }
                        type="number"
                        error={ !!error }
                      />
                      { error && <FormHelperText>{ error.message }</FormHelperText> }
                    </>
                  ) : (
                    <>
                      <InputLabel shrink id="originalAmount-label">{ t('reservation.originalAmount') }</InputLabel>
                      <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                        { field.value || t('common.na') }
                      </Typography>
                    </>
                  ) }
                </FormControl>
              ) }
            />

            <Controller
              name="discountedAmount"
              control={ methods.control }
              render={ ({ field, fieldState: { error } }) => (
                <FormControl fullWidth margin="normal" error={ !!error }>
                  { !(reservation.createdType === 'app-created') && isEditMode ? (
                    <>
                      <TextField
                        { ...field }
                        id="discountedAmount"
                        label={ t('reservation.discountedAmount') }
                        slotProps={ { inputLabel: { shrink: true } } }
                        type="number"
                        error={ !!error }
                      />
                      { error && <FormHelperText>{ error.message }</FormHelperText> }
                    </>
                  ) : (
                    <>
                      <InputLabel shrink id="discountedAmount-label">{ t('reservation.discountedAmount') }</InputLabel>
                      <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                        { field.value || t('common.na') }
                      </Typography>
                    </>
                  ) }
                </FormControl>
              ) }
            />

            { reservation.createdType === 'app-created' && (
              <Controller
                name="deductCredit"
                control={ methods.control }
                render={ ({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel shrink>{ t('reservation.creditDeducted') }</InputLabel>
                    <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                      { field.value || t('common.na') }
                    </Typography>
                  </FormControl>
                ) }
              />
            ) }

            <Controller
              name="remark"
              control={ methods.control }
              render={ ({ field, fieldState: { error } }) => (
                <FormControl fullWidth margin="normal" error={ !!error }>
                  <InputLabel shrink>{ t('reservation.remark') }</InputLabel>
                  { isEditMode ? (
                    <>
                      <TextField
                        { ...field }
                        id="remark"
                        label={ t('reservation.remark') }
                        multiline
                        rows={ 3 }
                        error={ !!error }
                        slotProps={ { inputLabel: { shrink: true } } }
                      />
                      { error && <FormHelperText>{ error.message }</FormHelperText> }
                    </>
                  ) : (
                    <Typography variant="body1" sx={ { mt: 2, ml: 2 } }>
                      { field.value || t('common.na') }
                    </Typography>
                  ) }
                </FormControl>
              ) }
            />

            <Box sx={ {
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              gap: 1
            } }>
              { !isEditMode ? (
                <>
                  <Button
                    onClick={ handleChatClick }
                    type="button"
                    color="primary"
                    startIcon={ <MessageIcon /> }
                  >
                    { t('reservationsPage.contactToRearrange') }
                  </Button>
                  <Button
                    onClick={ (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRearrange(reservation);
                    } }
                    type="button"
                    color="primary"
                  >
                    { t('actions.rearrange') }
                  </Button>
                  <Button
                    onClick={ (e) => {
                      e.preventDefault();
                      setIsEditMode(true);
                    } }
                    type="button"
                    color="primary"
                  >
                    { t('actions.edit') }
                  </Button>
                  { reservation.createdType === 'app-created' ? (
                    <Button
                      onClick={ (e) => {
                        e.preventDefault();
                        setShowCancelDialog(true);
                      } }
                      type="button"
                      color="error"
                    >
                      { t('actions.cancel') }
                    </Button>
                  ) : (
                    <Button
                      onClick={ (e) => {
                        e.preventDefault();
                        onDelete(reservation);
                        onClose();
                      } }
                      type="button"
                      color="error"
                    >
                      { t('common.delete') }
                    </Button>
                  ) }
                  <Button
                    onClick={ (e) => {
                      e.preventDefault();
                      onClose();
                    } }
                    type="button"
                    color="secondary"
                  >
                    { t('actions.close') }
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={ () => setIsEditMode(false) }
                    color="secondary"
                    disabled={ isSubmitting }
                  >
                    { t('actions.cancel') }
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={ isSubmitting }
                  >
                    { isSubmitting ? t('actions.saving') : t('actions.save') }
                  </Button>
                </>
              ) }
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      <CancelReservationDialog
        open={ showCancelDialog }
        onClose={ () => setShowCancelDialog(false) }
        onConfirm={ handleCancelConfirm }
      />

      <NotifyRearrangeDialog
        open={showNotifyDialog}
        onClose={handleCloseNotifyDialog}
        onConfirm={handleConfirmNotify}
        isNotifying={isNotifying}
        hasUserPhone={!!(reservation.customerPhone || reservation.userId?.phone)}
      />

      <Dialog
        open={ showChatDialog }
        onClose={ handleCloseChatDialog }
        maxWidth="md"
        fullWidth
        className="rearrange-dialog"
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { position: 'relative' } }>
            <MessageIcon color="primary" />
            <Typography variant="h6">
              { t('reservationsPage.rearrangeReservation') } { reservation.userId && `(${reservation.userId.displayName || reservation.userId.email})` }
            </Typography>
            <IconButton
              aria-label="close"
              onClick={ handleCloseChatDialog }
              sx={ {
                position: 'absolute',
                right: -15,
                top: -5,
                color: (theme) => theme.palette.grey[500],
              } }
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent className="conversation-dialog-content">
          { reservation.userId && (
            <ConversationPage userId={ reservation.userId._id } staffId={ reservation.staffId._id } />
          ) }
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReservationModal;
